import axios from 'axios';
import { castArray, filter, isEmpty, isFunction, isNil, isString, mapKeys } from 'lodash-es';

import { services } from 'services/base';
import {
  Configs,
  FeaturesData,
  GetConfigsService,
  GetMainConfigService,
  GetModulesConfigService,
  LoadFeaturesService,
  MainConfig,
  ModuleConfig,
  ModulesConfig,
  OnMainConfigChangeService,
  OnModulesConfigChangeService,
  ResetMainConfigService,
  SetMainConfigService,
  UpdateModulesConfigService,
} from 'types';
import { normalizeModules, registerStateServices } from 'utils';
import { convertExceptionToJson, getMetaValue, isAvailable } from 'utils/helpers';
import logger from 'utils/logger';

type ConfigState = {
  features: FeaturesData;
  originalConfigs: Configs;
  configs: Configs;
};

const PUBLIC_URL = process.env.PUBLIC_URL || '';
const SERVICE_PREFIX = 'appFrame.config';
const ACTION_SET_MAIN_CONFIG = 'AppFrame:Config:SetMainConfig';
const ACTION_SET_MODULES_CONFIG = 'AppFrame:Config:SetModulesConfig';

const initConfigServices = async () => {
  const { registerService, executeAction, registerAction, clearAction } = services;

  const state: ConfigState = {
    originalConfigs: {
      main: null,
      modules: null,
    },
    configs: {
      main: null,
      modules: null,
    },
    features: {},
  };

  registerStateServices({
    state,
    stateKey: 'features',
    servicePrefix: SERVICE_PREFIX,
    initValue: {},
    serviceMethods: {
      get: 'getFeatures',
      set: 'setFeatures',
      onChange: 'onFeaturesChange',
    },
  });

  const loadMainConfig = async () => {
    try {
      const response = await axios.get(getMetaValue('main-config') || `${PUBLIC_URL}/server-config.json`);
      executeAction(ACTION_SET_MAIN_CONFIG, response.data);
    } catch (error) {
      logger.error('Error loading appframe main configuration!', error);
      executeAction(ACTION_SET_MAIN_CONFIG, null);
      services.appFrame.error.main.addError({
        title: 'errors.load_error.title.main_config',
        message: 'errors.common_message',
        details: {
          type: 'MAIN_LOAD_ERROR',
          timestamp: new Date(),
          error: convertExceptionToJson(error),
        },
      });
    }
  };

  const loadModulesConfig = async () => {
    try {
      const { data: modules } = await axios.get<ModulesConfig>(
        getMetaValue('modules-config') || `${PUBLIC_URL}/modules-config.json`,
      );
      if (!modules || isEmpty(modules)) {
        throw new Error('Modules configuration is empty!');
      }

      const normalizedModules = normalizeModules(castArray(modules));
      state.configs.modules = normalizedModules;
      state.originalConfigs.modules = normalizedModules;
    } catch (error) {
      logger.error('Unable to load modules configuration!', error);
      state.configs.modules = null;
      state.originalConfigs.modules = null;
      state.features = {};
      services.appFrame.error.main.addError({
        title: 'errors.load_error.title.modules_config',
        message: 'errors.common_message',
        details: {
          type: 'MODULES_LOAD_ERROR',
          timestamp: new Date(),
          error: convertExceptionToJson(error),
        },
      });
    }
  };

  const loadAppConfigs = () => {
    const appConfigs = getMetaValue('app-configs')?.split(';') || [];
    const result = filter(
      appConfigs.map((entry) => {
        const [, moduleId, url] = entry.match(/^\s*(.+?)\s*=\s*(.+?)\s*$/) || [];
        if (!moduleId) {
          return null;
        }

        return axios
          .get(url)
          .then((response) => {
            const cfg = response.data;
            state.configs[moduleId] = cfg;
            state.originalConfigs[moduleId] = cfg;
          })
          .catch((err) => {
            logger.error(`Unable to load configuration for app module '${moduleId}' (${url})!`, err);
            state.configs[moduleId] = null;
            state.originalConfigs[moduleId] = null;
          });
      }),
    );

    return result;
  };

  registerService<GetMainConfigService>(`${SERVICE_PREFIX}.getMainConfig`, (original?: boolean) =>
    original ? state.originalConfigs.main : state.configs.main,
  );

  registerAction<MainConfig>(
    ACTION_SET_MAIN_CONFIG,
    (newConfig) => {
      if (isNil(state.configs.main)) {
        state.originalConfigs.main = newConfig;
      }
      state.configs.main = newConfig;
    },
    { order: 0 },
  );

  registerService<SetMainConfigService>(`${SERVICE_PREFIX}.setMainConfig`, (configOrCallback) => {
    const { configs, originalConfigs } = state;
    const newConfig = isFunction(configOrCallback)
      ? configOrCallback(configs.main, originalConfigs.main)
      : configOrCallback;
    executeAction(ACTION_SET_MAIN_CONFIG, newConfig);
  });

  registerService<OnMainConfigChangeService>(`${SERVICE_PREFIX}.onMainConfigChange`, (callback) => {
    registerAction(ACTION_SET_MAIN_CONFIG, callback, { order: 1 });
    return () => clearAction(ACTION_SET_MAIN_CONFIG, callback);
  });

  registerService<ResetMainConfigService>(`${SERVICE_PREFIX}.resetMainConfig`, () => {
    executeAction(ACTION_SET_MAIN_CONFIG, state.originalConfigs.main);
  });

  registerService<GetConfigsService>(`${SERVICE_PREFIX}.getConfigs`, () => state.configs);

  registerService<GetModulesConfigService>(`${SERVICE_PREFIX}.getModulesConfig`, () => state.configs.modules);

  registerAction<ModulesConfig>(
    ACTION_SET_MODULES_CONFIG,
    (newConfig) => {
      if (isNil(state.configs.modules)) {
        state.originalConfigs.modules = newConfig;
      }
      state.configs.modules = newConfig;
    },
    { order: 0 },
  );

  registerService<UpdateModulesConfigService>(`${SERVICE_PREFIX}.updateModulesConfig`, (moduleConfigs) => {
    if (!state.configs.modules) {
      throw Error('Unable to update modules configuration: config is not yet loaded!');
    }
    const modulesConfigChanges: ModulesConfig = castArray(moduleConfigs);
    const changeMapById = mapKeys(modulesConfigChanges, 'id');
    const changeMapByKey = mapKeys(modulesConfigChanges, 'key');
    const newModulesConfig = normalizeModules(
      state.configs.modules.map((moduleConfig) => {
        const { id, key } = moduleConfig;
        const configToChange: ModuleConfig | null = (!isNil(key) && changeMapByKey[key]) || changeMapById[id] || null;
        if (!configToChange) {
          return moduleConfig;
        }

        return {
          ...configToChange,
          id,
          key,
        };
      }),
    );

    executeAction(ACTION_SET_MODULES_CONFIG, newModulesConfig);
  });

  registerService<OnModulesConfigChangeService>(`${SERVICE_PREFIX}.onModulesConfigChange`, (callback) => {
    registerAction(ACTION_SET_MODULES_CONFIG, callback, { order: 1 });
    return () => clearAction(ACTION_SET_MODULES_CONFIG, callback);
  });

  registerService<LoadFeaturesService>(`${SERVICE_PREFIX}.loadFeatures`, async () => {
    if (!state.configs.modules) {
      return;
    }

    const features = { ...state.features };

    // load features config
    await Promise.all(
      state.configs.modules.map(({ id, featureUrl, available }) => {
        if (!isString(featureUrl) || !isAvailable(available)) {
          return null;
        }
        const token = services.appFrame.auth.getAccessToken();
        const config = token ? { headers: { Authorization: `Bearer ${token}` } } : undefined;

        return axios.get(featureUrl, config).then((res) => {
          features[id] = res.data;
        });
      }),
    );

    services.appFrame.config.setFeatures(features);
  });

  await Promise.all([loadMainConfig(), loadModulesConfig(), ...loadAppConfigs()]);
};

export default initConfigServices;
